import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ScrollText, Sparkles, Loader2, CheckCircle2, Download, RotateCcw, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import { ProductRequirements } from '../services/workshop';

// ---------- Types ----------

export interface SessionSummaryPersona {
  role: string;
  currentState: string;
  painPoints: string[];
  whereItHelps: string[];
}

export interface SessionSummaryData {
  sessionId: string;
  variant: 'full' | 'structured';
  status: 'generating' | 'draft' | 'published';
  generatedAt: string;
  publishedAt?: string;
  decisions?: Array<{ text: string }>;
  stats?: {
    avgProcessingMins?: number;
    rejectionRate?: number;
    multipleConditionsPct?: number;
    [key: string]: number | undefined;
  };
  personas?: SessionSummaryPersona[];
  nextSteps?: Array<{ owner?: string; text: string }>;
  markdownContent?: string;
  exportUrl?: string;
}

type SummaryVariant = 'structured' | 'full';

interface SessionSummaryPanelProps {
  sessionId: string;
  projectId?: string;
  sessionName?: string;
  isReadOnly?: boolean;
  hasTranscript: boolean;
  requirements?: ProductRequirements | null;
}

// ---------- Markdown typography (matches ReportSectionCard) ----------

const mdComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-slate-900 mt-6 mb-3">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-slate-800 mt-6 mb-2 pb-1 border-b border-slate-200">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-700 mt-4 mb-1.5">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-slate-600 mt-3 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700 leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-500">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-primary/40 pl-4 my-3 text-sm italic text-slate-600 bg-slate-50 py-2 rounded-r">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 text-sm text-slate-700 border-b border-slate-100">{children}</td>,
  hr: () => <hr className="my-5 border-slate-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-primary underline hover:no-underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

// ---------- Structured → markdown conversion ----------

const PERSONA_LABELS: Record<string, string> = {
  medical_assessor: 'Medical Assessor',
  deciding_officer: 'Deciding Officer',
  appeals_officer: 'Appeals Officer',
};

function structuredToMarkdown(summary: SessionSummaryData): string {
  const parts: string[] = [];

  if (summary.decisions && summary.decisions.length > 0) {
    parts.push('## What We Decided\n');
    summary.decisions.forEach((d, i) => {
      parts.push(`${i + 1}. ${d.text}`);
    });
    parts.push('');
  }

  if (summary.stats && Object.values(summary.stats).some(v => v !== undefined)) {
    parts.push('## By the Numbers\n');
    if (summary.stats.avgProcessingMins !== undefined)
      parts.push(`- **Average processing time:** ${summary.stats.avgProcessingMins} min per case`);
    if (summary.stats.rejectionRate !== undefined)
      parts.push(`- **Rejection rate:** ${summary.stats.rejectionRate}%`);
    if (summary.stats.multipleConditionsPct !== undefined)
      parts.push(`- **Multiple conditions:** ${summary.stats.multipleConditionsPct}%`);
    parts.push('');
  }

  if (summary.personas && summary.personas.length > 0) {
    parts.push('## Persona Journeys\n');
    for (const p of summary.personas) {
      const label = PERSONA_LABELS[p.role] || p.role.replace(/_/g, ' ');
      parts.push(`### ${label}\n`);
      parts.push(`${p.currentState}\n`);
      if (p.painPoints.length > 0) {
        parts.push('**Pain points:**\n');
        p.painPoints.forEach(pt => parts.push(`- ${pt}`));
        parts.push('');
      }
      if (p.whereItHelps.length > 0) {
        parts.push('**Where this helps:**\n');
        p.whereItHelps.forEach(pt => parts.push(`- ${pt}`));
        parts.push('');
      }
    }
  }

  if (summary.nextSteps && summary.nextSteps.length > 0) {
    parts.push('## Next Steps\n');
    summary.nextSteps.forEach(s => {
      const prefix = s.owner ? `**${s.owner}:** ` : '';
      parts.push(`- ${prefix}${s.text}`);
    });
    parts.push('');
  }

  return parts.join('\n');
}

function getMarkdown(summary: SessionSummaryData): string {
  if (summary.markdownContent) return summary.markdownContent;
  if (summary.variant === 'structured') return structuredToMarkdown(summary);
  return '';
}

// ---------- Minimal header ----------

function PanelHeader({
  active,
  onChange,
  hasStructured,
  hasFull,
  summary,
  isReadOnly,
  isGenerating,
  onPublish,
  onRegenerate,
  onDownload,
  publishing,
}: {
  active: SummaryVariant;
  onChange: (v: SummaryVariant) => void;
  hasStructured: boolean;
  hasFull: boolean;
  summary: SessionSummaryData | null;
  isReadOnly?: boolean;
  isGenerating: boolean;
  onPublish: () => void;
  onRegenerate: () => void;
  onDownload: () => void;
  publishing: boolean;
}) {
  const tabs: { id: SummaryVariant; label: string }[] = [
    { id: 'structured', label: 'Highlights' },
    { id: 'full', label: 'Full Recap' },
  ];
  const isDraft = summary?.status === 'draft';
  const isPublished = summary?.status === 'published';

  return (
    <div className="shrink-0 flex items-center justify-between border-b border-slate-200 bg-white px-4">
      <div className="flex">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
              active === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            {t.label}
            {(t.id === 'structured' ? hasStructured : hasFull) && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
            )}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        {isDraft && (
          <span className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            Draft
          </span>
        )}
        {isPublished && (
          <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            Published
          </span>
        )}
        {summary && !isReadOnly && (
          <button
            onClick={onRegenerate}
            disabled={isGenerating}
            title="Regenerate"
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
          </button>
        )}
        {summary?.exportUrl && (
          <button onClick={onDownload} title="Download DOCX" className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors">
            <Download size={13} />
          </button>
        )}
        {isDraft && !isReadOnly && (
          <button
            onClick={onPublish}
            disabled={publishing}
            className="flex items-center gap-1 ml-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {publishing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            Publish
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Empty / generating state ----------

function VariantEmptyState({
  variant,
  hasTranscript,
  isReadOnly,
  generating,
  error,
  onGenerate,
}: {
  variant: SummaryVariant;
  hasTranscript: boolean;
  isReadOnly?: boolean;
  generating: boolean;
  error: string | null;
  onGenerate: () => void;
}) {
  const isStructured = variant === 'structured';
  return (
    <div className="flex flex-col items-center justify-center flex-1 gap-4 p-8 text-center">
      {generating ? (
        <>
          <Loader2 size={28} className="text-primary animate-spin" />
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-slate-700">Generating summary…</p>
            <p className="text-xs text-slate-400">Analysing transcript, images, user stories, and session graph.</p>
          </div>
        </>
      ) : (
        <>
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <ScrollText size={20} className="text-primary" />
          </div>
          <div className="flex flex-col gap-1 max-w-xs">
            <p className="text-sm font-semibold text-slate-700">
              {isStructured ? 'No highlights summary yet' : 'No full recap yet'}
            </p>
            <p className="text-xs text-slate-400 leading-relaxed">
              {isStructured
                ? 'Generates key decisions, persona journeys, and next steps as a readable document.'
                : 'Generates a full narrative session record covering the complete arc of the day.'}
            </p>
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 max-w-xs">
              <AlertCircle size={13} className="shrink-0" />
              {error}
            </div>
          )}
          <button
            onClick={onGenerate}
            disabled={!hasTranscript || isReadOnly}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkles size={14} />
            {isStructured ? 'Generate Highlights' : 'Generate Full Recap'}
          </button>
          {!hasTranscript && (
            <p className="text-xs text-slate-400">No transcript available yet.</p>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Main panel ----------

export function SessionSummaryPanel({ sessionId, projectId, isReadOnly, hasTranscript }: SessionSummaryPanelProps) {
  const [activeVariant, setActiveVariant] = useState<SummaryVariant>('structured');
  const [summaries, setSummaries] = useState<Record<SummaryVariant, SessionSummaryData | null>>({
    structured: null,
    full: null,
  });
  const [generating, setGenerating] = useState<Record<SummaryVariant, boolean>>({ structured: false, full: false });
  const [publishing, setPublishing] = useState(false);
  const [errors, setErrors] = useState<Record<SummaryVariant, string | null>>({ structured: null, full: null });

  const pollingRefs = useRef<Record<SummaryVariant, ReturnType<typeof setInterval> | null>>({ structured: null, full: null });

  const stopPolling = useCallback((variant: SummaryVariant) => {
    if (pollingRefs.current[variant]) {
      clearInterval(pollingRefs.current[variant]!);
      pollingRefs.current[variant] = null;
    }
  }, []);

  const startPolling = useCallback((variant: SummaryVariant) => {
    stopPolling(variant);
    let attempts = 0;
    const MAX_ATTEMPTS = 60;
    pollingRefs.current[variant] = setInterval(async () => {
      attempts++;
      try {
        const res = await api.get<SessionSummaryData>(`/api/sessions/${sessionId}/summary`, { params: { variant } });
        const data = res.data;
        if (data.status !== 'generating') {
          stopPolling(variant);
          setGenerating(prev => ({ ...prev, [variant]: false }));
          setSummaries(prev => ({ ...prev, [variant]: data }));
        } else if (attempts >= MAX_ATTEMPTS) {
          stopPolling(variant);
          setGenerating(prev => ({ ...prev, [variant]: false }));
          setErrors(prev => ({ ...prev, [variant]: 'Generation timed out. Please try again.' }));
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          if (attempts >= MAX_ATTEMPTS) {
            stopPolling(variant);
            setGenerating(prev => ({ ...prev, [variant]: false }));
            setErrors(prev => ({ ...prev, [variant]: 'Generation timed out. Please try again.' }));
          }
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          stopPolling(variant);
          setGenerating(prev => ({ ...prev, [variant]: false }));
        }
      }
    }, 3000);
  }, [sessionId, stopPolling]);

  useEffect(() => {
    const initialLoad = async (variant: SummaryVariant) => {
      try {
        const res = await api.get<SessionSummaryData>(`/api/sessions/${sessionId}/summary`, { params: { variant } });
        const data = res.data;
        if (data.status === 'generating') {
          setGenerating(prev => ({ ...prev, [variant]: true }));
          startPolling(variant);
        } else {
          setSummaries(prev => ({ ...prev, [variant]: data }));
        }
      } catch (err: any) {
        if (err.response?.status === 404) return;
      }
    };
    initialLoad('structured');
    initialLoad('full');
    return () => { stopPolling('structured'); stopPolling('full'); };
  }, [sessionId, startPolling, stopPolling]);

  const handleGenerate = async (variant: SummaryVariant) => {
    if (generating[variant]) return;
    setGenerating(prev => ({ ...prev, [variant]: true }));
    setErrors(prev => ({ ...prev, [variant]: null }));
    setSummaries(prev => ({ ...prev, [variant]: null }));
    try {
      await api.post(`/api/sessions/${sessionId}/summary/generate`, { variant, projectId });
      startPolling(variant);
    } catch (e: any) {
      setErrors(prev => ({ ...prev, [variant]: e instanceof Error ? e.message : 'Something went wrong' }));
      setGenerating(prev => ({ ...prev, [variant]: false }));
    }
  };

  const handlePublish = async (variant: SummaryVariant) => {
    setPublishing(true);
    try {
      const res = await api.post<SessionSummaryData>(`/api/sessions/${sessionId}/summary/publish`, { variant });
      setSummaries(prev => ({ ...prev, [variant]: res.data }));
    } catch (e: any) {
      setErrors(prev => ({ ...prev, [variant]: e instanceof Error ? e.message : 'Publish failed' }));
    } finally {
      setPublishing(false);
    }
  };

  const handleDownload = (summary: SessionSummaryData) => {
    if (summary.exportUrl) window.open(summary.exportUrl, '_blank');
  };

  const current = summaries[activeVariant];
  const isGenerating = generating[activeVariant];
  const markdown = current ? getMarkdown(current) : '';

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      <PanelHeader
        active={activeVariant}
        onChange={setActiveVariant}
        hasStructured={!!summaries.structured}
        hasFull={!!summaries.full}
        summary={current}
        isReadOnly={isReadOnly}
        isGenerating={isGenerating}
        onPublish={() => handlePublish(activeVariant)}
        onRegenerate={() => handleGenerate(activeVariant)}
        onDownload={() => handleDownload(current!)}
        publishing={publishing}
      />

      {current ? (
        <div className="flex-1 overflow-y-auto bg-slate-50 p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-8 py-6">
            {markdown
              ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{markdown}</ReactMarkdown>
              : <p className="text-sm text-slate-400 italic">No content available.</p>
            }
          </div>
        </div>
      ) : (
        <VariantEmptyState
          variant={activeVariant}
          hasTranscript={hasTranscript}
          isReadOnly={isReadOnly}
          generating={isGenerating}
          error={errors[activeVariant]}
          onGenerate={() => handleGenerate(activeVariant)}
        />
      )}
    </div>
  );
}
